/* collection-filters.js — July 2025 refactor
 *
 * Unified **AJAX sorting** & **faceted filtering** for Shopify collection pages.
 * 
 *  ▸ Cancels overlapping requests with **AbortController**
 *  ▸ Uses **event delegation** so new DOM doesn’t need manual re‑binding
 *  ▸ Applies desktop price ranges only when the visitor confirms them
 *  ▸ Re‑initialises itself automatically after every HTML swap
 *  ▸ Pure vanilla JS — no extra deps
 *
 * Sections
 *  1. Tiny helpers (qs, qsa)
 *  2. AjaxPage – wrapper around fetch+HTML+abort
 *  3. Dropdown  – generic .is-open toggler
 *  4. SortSelect– custom <select> replacement for “Sort by …”
 *  5. FiltersManager – all checkbox/price/chip logic
 *  6. Bootstrapping on DOMContentLoaded
 *
 * Edit points
 *  – Change selectors in SortSelect & FiltersManager if your theme differs.
 *  – Add polyfills for AbortController/fetch if you support IE.
 */

// ─────────────────────────────────────── 1· helpers
const qs = (sel, root = document) => root.querySelector(sel);
const qsa = (sel, root = document) => [...root.querySelectorAll(sel)];
function updateCollectionLinks(add = true) {
  const links = qsa('a[href*="/collections/"]');
  const origin = location.origin;

  links.forEach(a => {
    try {
      const u = new URL(a.getAttribute('href'), origin);
      if (u.origin !== origin) return;
      if (!u.pathname.startsWith('/collections/')) return;
      
      if (add) {
        if (u.searchParams.get('filter.v.availability') !== '1') {
          u.searchParams.set('filter.v.availability', '1');
          a.setAttribute('href', u.pathname + '?' + u.searchParams.toString());
        }
      } else {
        if (u.searchParams.has('filter.v.availability')) {
          u.searchParams.delete('filter.v.availability');
          const newUrl = u.searchParams.toString() 
            ? u.pathname + '?' + u.searchParams.toString()
            : u.pathname;
          a.setAttribute('href', newUrl);
        }
      }
    } catch (e) { /* noop */ }
  });
}

function syncURLFilterChips() {
  const layout = qs('.collection-layout');
  if (!layout) return;
  layout.classList.toggle('has-empty-results', Boolean(qs('.collection-empty', layout)));
  const url = new URL(location);
  const active = [...url.searchParams.entries()].filter(([name, value]) => name.startsWith('filter.') && value !== '');
  if (!active.length) return;

  let section = qs('.active-filters-section');
  if (!section) {
    section = document.createElement('div');
    section.className = 'active-filters-section';
    const wrapper = document.createElement('div');
    wrapper.className = 'active-filters-wrapper';
    const heading = document.createElement('span');
    heading.className = 'active-filters-label';
    heading.textContent = layout.dataset.activeFiltersLabel || 'Filters:';
    const chips = document.createElement('div');
    chips.className = 'active-filters-chips';
    wrapper.append(heading, chips);
    section.appendChild(wrapper);
    layout.insertBefore(section, qs('.collection-toolbar', layout));
  }

  const chips = qs('.active-filters-chips', section);
  const covered = new Set();
  qsa('.active-filter-chip[data-filter-remove]', chips).forEach(chip => {
    try {
      const removed = new URL(chip.dataset.filterRemove, url).searchParams;
      active.forEach(([name, value]) => {
        const originalCount = url.searchParams.getAll(name).filter(item => item === value).length;
        const removedCount = removed.getAll(name).filter(item => item === value).length;
        if (removedCount < originalCount) covered.add(name + '\u0000' + value);
      });
    } catch (error) { /* Ignore an invalid legacy chip URL. */ }
  });

  const addChip = (label, removalURL) => {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'active-filter-chip';
    chip.dataset.filterRemove = removalURL.pathname + removalURL.search;
    const chipLabel = document.createElement('span');
    chipLabel.className = 'active-filter-chip__label';
    chipLabel.textContent = label;
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 10 10');
    icon.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M8 2L2 8M2 2l6 6');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linecap', 'round');
    icon.appendChild(path);
    chip.append(chipLabel, icon);
    chips.appendChild(chip);
  };

  if (active.some(([name, value]) => name.startsWith('filter.v.price.') && !covered.has(name + '\u0000' + value))) {
    const removalURL = new URL(url);
    [...removalURL.searchParams.keys()].filter(name => name.startsWith('filter.v.price.'))
      .forEach(name => removalURL.searchParams.delete(name));
    removalURL.searchParams.delete('page');
    const symbol = layout.dataset.currencySymbol || '';
    const minimum = url.searchParams.get('filter.v.price.gte') || '0';
    const maximum = url.searchParams.get('filter.v.price.lte');
    const label = `${layout.dataset.priceLabel || 'Price'}: ${symbol}${minimum}${maximum ? ' – ' + symbol + maximum : '+'}`;
    addChip(label, removalURL);
  }

  active.forEach(([name, value]) => {
    if (name.startsWith('filter.v.price.') || covered.has(name + '\u0000' + value)) return;
    const removalURL = new URL(url);
    const values = removalURL.searchParams.getAll(name);
    values.splice(values.indexOf(value), 1);
    removalURL.searchParams.delete(name);
    values.forEach(item => removalURL.searchParams.append(name, item));
    removalURL.searchParams.delete('page');
    const option = qsa('#FacetFiltersForm input[type="checkbox"]').find(input => input.name === name && input.value === value);
    const optionLabel = option?.closest('.facets__option')?.querySelector('.facets__option-label')?.textContent.trim();
    const label = name === 'filter.v.availability'
      ? layout.dataset.availabilityLabel || optionLabel || value
      : optionLabel || value;
    addChip(label, removalURL);
  });

  let clear = qs('.active-filters-clear', section);
  if (!clear) {
    clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'active-filters-clear';
    clear.textContent = layout.dataset.clearFiltersLabel || 'Clear all';
    qs('.active-filters-wrapper', section).appendChild(clear);
  }
  const clearURL = new URL(url);
  [...clearURL.searchParams.keys()].filter(name => name.startsWith('filter.'))
    .forEach(name => clearURL.searchParams.delete(name));
  clearURL.searchParams.delete('page');
  clear.dataset.clearUrl = clearURL.pathname + clearURL.search;
  const emptyClear = qs('.collection-empty__clear');
  if (emptyClear) {
    emptyClear.href = clear.dataset.clearUrl;
    emptyClear.dataset.clearUrl = clear.dataset.clearUrl;
  }
}

// ─────────────────────────────────────── 2· Ajax wrapper
class AjaxPage {
  static abort;
  /** Fetch HTML and return text. Concurrent calls abort previous one. */
  static async fetchHTML(url) {
    AjaxPage.abort?.abort();
    AjaxPage.abort = new AbortController();
    try {
      const res = await fetch(url, {
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        signal: AjaxPage.abort.signal,
      });
      if (!res.ok) throw new Error(res.status);
      return res.text();
    } catch (err) {
      if (err.name === 'AbortError') return null; // caller fired a newer request
      throw err;
    }
  }
}

// ─────────────────────────────────────── 3· Generic dropdown helper
class Dropdown {
  constructor(wrapper, triggerSel = '.trigger') {
    this.wrapper = wrapper;
    this.trigger = qs(triggerSel, wrapper);
    if (!this.trigger) return; // silently skip if structure is wrong
    
    this.trigger.addEventListener('click', e => {
      
      e.preventDefault();
      const isOpen = this.wrapper.classList.contains('is-open');
      Dropdown.closeAll();
      if (!isOpen) this.wrapper.classList.add('is-open'); // open only if was closed
      
    });
  }
  static closeAll() {
    qsa('.is-open').forEach(el => {
      // Keep drawer overlay open but allow dropdowns inside it to close
      if (el.id === 'mobile-drawer-overlay' || el.id === 'mobile-drawer-filters') return;
      el.classList.remove('is-open');
    });
  }
}

// ─────────────────────────────────────── 4· Sort dropdown
class SortSelect {
  constructor(el, contentSel = '.collection-layout') {
    this.el = el;
    this.options = qs('.custom-select__options', el);
    this.hiddenSel = qs('select', el);
    this.contentEl = qs(contentSel);

    new Dropdown(el, '.custom-select__trigger');
    el.addEventListener('click', e => {
      const opt = e.target.closest('.custom-select__option');
      if (opt) this.choose(opt);
    });
  }
  /** Mark selected option + fire AJAX */
  choose(opt) {
    this.options.querySelectorAll('.is-active').forEach(o => o.classList.remove('is-active'));
    opt.classList.add('is-active');
    this.hiddenSel.value = opt.dataset.value;
    this.update(opt.dataset.value);
  }
  async update(value) {
    const url = new URL(location);
    url.searchParams.set('sort_by', value);
    await FiltersManager.updateContent(url, this.contentEl, true);
  }
}

// ─────────────────────────────────────── 5· Faceted filters
class FiltersManager {
  /* rebind dropdown triggers on a given root */
  static bindDropdowns(root){
    
    qsa('.facets__trigger', root).forEach(t=>{
      
      if(!t.dataset.dropdownBound){
        t.dataset.dropdownBound='1';
        new Dropdown(t.parentElement, '.facets__trigger');
      }
    });
  }
  constructor(rootSel = '.facets') {
    // reference to apply button (mobile)
    this.applyBtn = qs('#drawer-apply-filters');
    this.root = qs(rootSel);
    this.contentEl = qs('.collection-layout');
    FiltersManager.current = this;
    FiltersManager.bindGlobalHandlers();
    if (!this.root) return;          // no facets on this page
    this.bind();
    this.syncStates();
  }

  static bindGlobalHandlers() {
    if (FiltersManager.globalHandlersBound) return;
    FiltersManager.globalHandlersBound = true;
    document.addEventListener('drawer-closed', () => FiltersManager.current?.refreshCounts());
    qs('#drawer-apply-filters')?.addEventListener('click', () => {
      FiltersManager.current?.update(true).then(applied => {
        if (applied) MobileDrawer.close();
      });
    });
    document.addEventListener('click', e => {
      const remove = e.target.closest('[data-filter-remove], [data-clear-url]');
      if (remove) {
        e.preventDefault();
        FiltersManager.current?.remove(remove.dataset.filterRemove || remove.dataset.clearUrl);
      }
      if (!e.target.closest('#FacetFiltersForm, .facets, .custom-select, #mobile-drawer-filters')) Dropdown.closeAll();
    });
  }

  // delegate once
  bind() {
    // ensure dropdowns bound initially
    FiltersManager.bindDropdowns(this.root);
    // 5.1 checkboxes
    this.root.addEventListener('change', e => {
      if (FiltersManager.current !== this) return;
      if (e.target.matches('input[type="checkbox"]')) {
        if (e.target.name === 'filter.v.availability') {
          try {
            if (e.target.checked) {
              localStorage.removeItem('availability_filter_dismissed');
              updateCollectionLinks(true);
            } else {
              localStorage.setItem('availability_filter_dismissed', 'true');
              updateCollectionLinks(false);
            }
          } catch (err) { /* noop */ }
        }
        
        if (MobileDrawer?.mq?.matches && MobileDrawer.overlay?.classList.contains('is-open')) {
          this.refreshCounts();
        } else {
          this.update(false, true);
        }
      }
    });

    // 5.2 price fields are drafts until Apply is clicked.
    this.root.addEventListener('input', e => {
      if (FiltersManager.current !== this) return;
      if (!e.target.matches('.facets__option--price-range input[type="number"]')) return;
      this.lastEditedPriceInput = e.target;
      if (MobileDrawer?.mq?.matches && MobileDrawer.overlay?.classList.contains('is-open')) this.refreshCounts();
    });
    this.root.addEventListener('click', e => {
      if (FiltersManager.current !== this) return;
      const button = e.target.closest('.price-range__apply');
      if (!button || !this.root.contains(button)) return;
      e.preventDefault();
      this.applyPriceRange(button);
    });
    this.root.addEventListener('keydown', e => {
      if (FiltersManager.current !== this) return;
      if (e.key !== 'Enter' || !e.target.matches('.facets__option--price-range input[type="number"]')) return;
      if (MobileDrawer?.mq?.matches && MobileDrawer.overlay?.classList.contains('is-open')) return;
      e.preventDefault();
      this.applyPriceRange(e.target);
    });
    this.root.addEventListener('submit', e => {
      if (FiltersManager.current !== this) return;
      e.preventDefault();
      if (MobileDrawer?.mq?.matches && MobileDrawer.overlay?.classList.contains('is-open')) return;
      this.applyPriceRange(this.lastEditedPriceInput || this.root.querySelector('.facets__option--price-range'));
    });

  }

  /* utilities */
  inputs() { return qsa('input', this.root); }

  buildURL(applyPriceDraft = false) {
    const url = new URL(location);
    const inputs = this.inputs();
    const appliedPrices = new Map(qsa('.facets__option--price-range input[type="number"]', this.root)
      .map(input => [input.name, url.searchParams.get(input.name)]));
    const unavailableFilters = [...url.searchParams.entries()].filter(([name, value]) =>
      name.startsWith('filter.') && !inputs.some(input => input.name === name &&
        (input.type !== 'checkbox' || input.value === value)));
    // wipe existing filter params
    [...url.searchParams.keys()].filter(k => k.startsWith('filter.')).forEach(k => url.searchParams.delete(k));

    inputs.forEach(i => {
      if (i.type === 'checkbox' && i.checked) url.searchParams.append(i.name, i.value);
      if (i.type === 'number') {
        const value = applyPriceDraft ? i.value : appliedPrices.get(i.name);
        if (value) url.searchParams.set(i.name, value);
      }
    });
    unavailableFilters.forEach(([name, value]) => url.searchParams.append(name, value));
    return url;
  }

  async update(applyPriceDraft = false, preservePriceDraft = false) {
    if (applyPriceDraft) {
      const editedInput = this.lastEditedPriceInput?.isConnected
        ? this.lastEditedPriceInput
        : qs('.facets__option--price-range input[type="number"]', this.root);
      if (editedInput && !this.adjustRange(editedInput)) return false;
    }
    await FiltersManager.updateContent(this.buildURL(applyPriceDraft), this.contentEl, preservePriceDraft);
    return true;
  }
  applyPriceRange(source) {
    if (source?.matches('input[type="number"]')) this.lastEditedPriceInput = source;
    this.update(true);
  }
  async remove(href) {
    const url = new URL(href, location.origin);
    const currentURL = new URL(location);
    const hasAvailabilityNow = currentURL.searchParams.get('filter.v.availability') === '1';
    const hasAvailabilityAfter = url.searchParams.get('filter.v.availability') === '1';
    
    if (hasAvailabilityNow && !hasAvailabilityAfter) {
      try {
        localStorage.setItem('availability_filter_dismissed', 'true');
        updateCollectionLinks(false);
      } catch (e) { /* noop */ }
    }
    
    await FiltersManager.updateContent(url, this.contentEl);
  }

  syncStates() {
    const appliedParams = new URL(location).searchParams;
    qsa('.facets__option', this.root).forEach(opt => {
      const cb = qs('input[type="checkbox"]', opt);
      const priceInputs = qsa('input[type="number"]', opt);
      
      // Check if checkbox is selected
      const cbSelected = cb?.checked;
      
      // Check if price range has values
      const priceSelected = priceInputs.some(input => Boolean(appliedParams.get(input.name)));
      
      const selected = cbSelected || priceSelected;
      opt.classList.toggle('is-active', selected);
      opt.classList.toggle('is-selected', selected);
    });
  }

  /** Update badge counts and button visibility */
  refreshCounts(){
    const appliedParams = new URL(location).searchParams;
    const showPriceDraft = MobileDrawer?.mq?.matches && MobileDrawer.overlay?.classList.contains('is-open');
    // count selected inputs per trigger
    qsa('.facets-container', this.root).forEach(container=>{
      const trigger=qs('.facets__trigger, .facets__availability',container);
      if(!trigger) return;
      const opts=qsa('.facets__option',container);
      let selected=0;
      opts.forEach(o=>{
        const cb=qs('input[type="checkbox"]',o);
        const priceInputs=qsa('input[type="number"]',o);
        
        // Check if checkbox is selected
        const cbSelected=cb?.checked;
        
        // Check if price range has values
        const priceSelected=priceInputs.some(input=>Boolean(showPriceDraft ? input.value : appliedParams.get(input.name)));
        
        const isSel=cbSelected || priceSelected;
        o.classList.toggle('is-selected', isSel);
        if(isSel) selected++;
      });
      // add is-selected class to trigger
      trigger.classList.toggle('has-selected', selected>0);
      let badge=qs('.facets-count-badge',trigger);
      if(selected && !trigger.classList.contains('facets__availability')){
        if(!badge){
          badge=document.createElement('span');
          badge.className='facets-count-badge';
          trigger.insertBefore(badge, trigger.querySelector('.facets__arrow'));
        }
        badge.textContent=selected;
      }else if(badge){
        badge.remove();
      }
    });
    // toggle apply button - check both checkboxes and price inputs
    const anySelected=this.inputs().some(i=>{
      if(i.type==='checkbox') return i.checked;
      if(i.type==='number') return i.value && i.value.trim()!=='';
      return false;
    });
    if(this.applyBtn){
      this.applyBtn.classList.toggle('drawer-apply--visible', anySelected);

    }
  }

  /** Keep min ≤ max & update HTML constraints */
  adjustRange(input) {
    const wrapper = input.closest('.facets__option--price-range');
    if (!wrapper) return true;
    const fields = qsa('input[type="number"]', wrapper);
    if (fields.length !== 2) return true;
    const [minI, maxI] = fields;

    const minVal = parseFloat(minI.value.replace(',', '.'));
    const maxVal = parseFloat(maxI.value.replace(',', '.'));

    // update HTML min/max to guide native validation
    maxI.min = minI.value || 0;
    minI.max = maxI.value || maxI.max || '';

    // Keep the entered values intact and ask for a valid range before filtering.
    if (!isNaN(minVal) && !isNaN(maxVal) && minVal > maxVal) {
      input.reportValidity();
      return false;
    }
    const invalidField = fields.find(field => !field.checkValidity());
    if (invalidField) {
      invalidField.reportValidity();
      return false;
    }
    return true;
  }

  // shared static so SortSelect can reuse
  static async updateContent(url, container, preservePriceDraft = false) {
    const currentParams = new URL(location).searchParams;
    const priceDraft = preservePriceDraft
      ? qsa('#FacetFiltersForm .facets__option--price-range input[type="number"]')
          .filter(input => input.value !== (currentParams.get(input.name) || ''))
          .map(input => [input.name, input.value])
      : [];
    // visual loading feedback via CSS class (more reliable)
    container.classList.add('is-loading');

    try {
      const html = await AjaxPage.fetchHTML(url);
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = qs('.collection-layout', doc);
        if (!next) {
          location.href = url;
          return;
        }
        container.innerHTML = next.innerHTML;

        const currentSearchSummary = qs('.search-top-block > p');
        const nextSearchSummary = qs('.search-top-block > p', doc);
        if (currentSearchSummary && nextSearchSummary) currentSearchSummary.replaceWith(nextSearchSummary);
        const currentSearchContent = qs('.search-content-results');
        const nextSearchContent = qs('.search-content-results', doc);
        if (currentSearchContent && nextSearchContent) currentSearchContent.replaceWith(nextSearchContent);
        else if (currentSearchContent) currentSearchContent.remove();

        // replace facet filters form so options list is in sync
        const nextForm = qs('#FacetFiltersForm', doc);
        if (nextForm) {
          priceDraft.forEach(([name, value]) => {
            const field = qsa('.facets__option--price-range input[type="number"]', nextForm)
              .find(input => input.name === name);
            if (field) field.value = value;
          });
          const currentForm = qs('#FacetFiltersForm');
          currentForm?.replaceWith(nextForm);
        }

        // replace active filters chips section
        const currentChips = qs('.active-filters-section');
        const nextChips = qs('.active-filters-section', doc);
        if (nextChips) {
          // Clone to remove previous listeners, avoids double activations
          if (currentChips) currentChips.replaceWith(nextChips);
          else container.insertBefore(nextChips, qs('.collection-toolbar', container));
        } else {
          // If no chips in response, remove existing chips
          // Si ya no hay filtros activos en la respuesta, eliminamos los chips actuales
          currentChips?.remove();
        }

        history.pushState({}, '', url);

        syncURLFilterChips();
        new FiltersManager();
        qsa('.custom-select').forEach(sel => new SortSelect(sel));
        MobileDrawer.reinit();

        const hasAvailabilityFilter = url.searchParams.get('filter.v.availability') === '1';
        updateCollectionLinks(hasAvailabilityFilter);

        container.classList.remove('is-loading');
      }
    } catch (err) {
      console.error('AJAX failed, fallback to full load', err);
      location.href = url;
    }
  }
}


// ─────────────────────────────────────── 6· Mobile drawer (only addition)
class MobileDrawer {
  static init() {
    this.overlay = qs('#mobile-drawer-overlay');
    this.drawer = qs('#mobile-drawer-filters');
    this.toggle = qs('.filters-toggle');
    this.applyBtn = qs('#drawer-apply-filters');
    this.closeBtn = qs('.drawer-close', this.drawer);
    if (!this.drawer || !this.toggle) return;

    let rawBp = getComputedStyle(document.documentElement).getPropertyValue('--collapsedMenu').trim();
    // if the CSS var is not a valid length (e.g. "$collapsedMenu" literal), fall back
    const bp = /^\d/.test(rawBp) ? rawBp : '1320px';
    this.mq = window.matchMedia(`(max-width: ${bp})`);


    this.mq.addEventListener('change', e => { this.relocate(); });
    this.relocate();

    this.toggle.addEventListener('click', () => this.open());
    // close on Escape key
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) this.close();
    });
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', e => { if (e.target === this.overlay) this.close(); });
  }
  static open() {

    this.overlay.removeAttribute('hidden');
    this.overlay.setAttribute('aria-hidden', 'false');
    // Wait one frame so CSS transition triggers
    requestAnimationFrame(() => this.overlay.classList.add('is-open'));
    this.toggle.setAttribute('aria-expanded', 'true');

    // Freeze scroll without breaking sticky elements
    document.documentElement.classList.add('no-scroll');

  }
  static close() {

    document.dispatchEvent(new CustomEvent('drawer-closed'));
    this.applyBtn?.classList.remove('drawer-apply--visible');
    this.overlay.classList.remove('is-open');
    // wait for css transition (300ms) then hide
    setTimeout(() => {
      this.overlay.setAttribute('aria-hidden', 'true');
      this.overlay.setAttribute('hidden', '');
    }, 350);
    this.toggle.setAttribute('aria-expanded', 'false');

    // Restore scroll
    document.documentElement.classList.remove('no-scroll');

  }
  static relocate() {

    const form = qs('#FacetFiltersForm');
    if (!form) return;
    // remove any stale duplicate forms first
    qsa('#mobile-drawer-filters #FacetFiltersForm, .collection-filters-section #FacetFiltersForm').forEach(f => {
      if (f !== form) f.remove();
    });

    const chips = qs('.active-filters-section');
    // clean duplicates for chips
    if (chips) {
      qsa('#mobile-drawer-filters .active-filters-section, .collection-layout > .active-filters-section').forEach(el=>{
        if (el!==chips) el.remove();
      });
    }

    if (this.mq.matches) {
      // Keep recovery actions visible beside the empty result message.
      if (chips) {
        const layout = qs('.collection-layout');
        if (layout?.classList.contains('has-empty-results')) layout.insertBefore(chips, qs('.collection-toolbar', layout));
        else this.drawer.prepend(chips);
      }
      this.drawer.appendChild(form);
    } else {
      // desktop: chips back to top of layout, form to toolbar
      if (chips) qs('.collection-layout')?.prepend(chips);
      if (form) qs('.collection-filters-section')?.appendChild(form);
      this.close();
    }

    // rebind facet logic after DOM move
    new FiltersManager();
  }
  static reinit() {

    this.init();
  }
}


// ─────────────────────────────────────── 6· boot

document.addEventListener('DOMContentLoaded', () => {
  qsa('.custom-select').forEach(sel => new SortSelect(sel));
  syncURLFilterChips();
  new FiltersManager();
  MobileDrawer.init();
  
  try {
    const currentURL = new URL(location);
    const hasAvailabilityFilter = currentURL.searchParams.get('filter.v.availability') === '1';
    updateCollectionLinks(hasAvailabilityFilter);
  } catch (e) { /* noop */ }
});
